import React from 'react';
import { ChevronsUpDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useContracts } from '@/services/contractService';

interface ContractPickerProps {
  /** Selected contract ID, or '' for none */
  value: string;
  /** Display text for the current selection (e.g. "CON-2024-001 - Microsoft 365") */
  label?: string;
  /** Called with the chosen ID and its label; '' and undefined clear the selection */
  onChange: (id: string, label: string | undefined) => void;
  /** Contract whose subtree (itself and its amendments) must not be offered */
  excludeSubtreeOf?: string;
  placeholder?: string;
  ariaLabel?: string;
}

const DEBOUNCE_MS = 250;
const PAGE_SIZE = 20;

/**
 * Searchable contract picker built on Popover and the contracts list endpoint.
 *
 * Results come from the server (number or title search, 20 rows, by contract number) only while
 * the picker is open, and `excludeSubtreeOf` keeps the server from offering the contract being
 * edited or its amendments.
 */
interface PickerResultsProps {
  value: string;
  excludeSubtreeOf?: string;
  onSelect: (id: string, label: string | undefined) => void;
}

/**
 * Search box and result list. Lives inside the Popover content, so the query runs only while
 * the picker is open instead of on every form load.
 */
function PickerResults({ value, excludeSubtreeOf, onSelect }: PickerResultsProps) {
  const [search, setSearch] = React.useState('');
  const [debounced, setDebounced] = React.useState('');

  React.useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(search.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [search]);

  const { data, isLoading, isError } = useContracts({
    search: debounced || undefined,
    pageSize: PAGE_SIZE,
    pageNumber: 1,
    sortBy: 'contractNumber',
    sortOrder: 'asc',
    excludeSubtreeOf,
  });

  return (
    <>
      <Input
        autoFocus
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by number or title"
        aria-label="Search contracts"
        className="mb-2"
      />
      <div role="listbox" aria-label="Contracts" className="max-h-64 overflow-y-auto">
        {value && (
          <button
            type="button"
            role="option"
            aria-selected={false}
            onClick={() => onSelect('', undefined)}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-grey-500 hover:bg-grey-100"
          >
            <X className="h-3.5 w-3.5" />
            None
          </button>
        )}
        {isLoading && <p className="px-2 py-1.5 text-sm text-grey-500">Loading...</p>}
        {isError && <p className="px-2 py-1.5 text-sm text-destructive">Could not load contracts</p>}
        {!isLoading && !isError && data?.items.length === 0 && (
          <p className="px-2 py-1.5 text-sm text-grey-500">No matching contracts</p>
        )}
        {data?.items.map((contract) => {
          const text = `${contract.contractNumber} - ${contract.title}`;
          const selected = contract.id === value;
          return (
            <button
              key={contract.id}
              type="button"
              role="option"
              aria-selected={selected}
              onClick={() => onSelect(contract.id, text)}
              className={cn(
                'block w-full truncate rounded px-2 py-1.5 text-left text-sm hover:bg-grey-100',
                selected && 'bg-grey-100 font-medium',
              )}
            >
              {text}
            </button>
          );
        })}
      </div>
    </>
  );
}

export function ContractPicker({
  value,
  label,
  onChange,
  excludeSubtreeOf,
  placeholder = 'Select parent contract',
  ariaLabel = 'Parent contract',
}: ContractPickerProps) {
  const [open, setOpen] = React.useState(false);

  const select = (id: string, nextLabel: string | undefined) => {
    onChange(id, nextLabel);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          className={cn('w-full justify-between font-normal h-10', !value && 'text-grey-400')}
        >
          <span className="truncate">{value && label ? label : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-2" align="start">
        <PickerResults value={value} excludeSubtreeOf={excludeSubtreeOf} onSelect={select} />
      </PopoverContent>
    </Popover>
  );
}
